import { create } from "zustand"

import { IMessageSource } from "~/types/knowledge-os"

export interface PitchDeckV2ChatState {
  pptSessionId: string | null
  status: "idle" | "generating" | "complete" | "failed"
  statusKey: string
  slideImages: string[]
  expectedSlideCount: number
  errorMessage: string
  pptxS3Url?: string
  // True while an iterate is rendering — blocks the chat composer, same as
  // status==="generating" does for a fresh generate. Needs its own flag
  // because `status` is owned by the stream manager, which only runs for
  // generate, so it reads "complete" throughout an iterate.
  isIterating?: boolean
}

interface IUseOSAgentsIdStore {
  messages: { [key: string]: any }
  setMessages: (key: string, value: any) => void
  openChatHistroy: boolean
  setOpenChatHistroy: (value: boolean) => void
  showSplitter: any
  setShowSplitter: (value: any) => void
  elapsedTimes: { [key: string]: number }
  setElapsedTime: (key: string, value: number) => void
  resetOSAgentsIdStore: (sessionId: string) => void
  uploadedFiles: File[]
  setUploadedFiles: (value: File[]) => void
  removeUploadedFile: (files: File) => void
  clearUploadedFiles: () => void
  pendingMessage: string
  setPendingMessage: (value: string) => void
  quotedText: string | null
  setQuotedText: (value: string | null) => void
  sourcesDrawerData: IMessageSource[] | null
  setSourcesDrawerData: (sources: IMessageSource[] | null) => void
  pitchDeckSlideImages: string[]
  setPitchDeckSlideImages: (urls: string[]) => void
  /**
   * Deck title as last read from the ppt_sessions row, keyed by ppt session id
   * (ENG-2504). Keyed rather than global so a deck's title can never bleed into
   * another opportunity's window while its own session is still loading.
   */
  pitchDeckTitleBySession: Record<string, string>
  setPitchDeckTitle: (pptSessionId: string, title: string) => void
  /** True while a generated page is rendering or an edit is applying, keyed on
   *  chat id. Blocks the chat composer, as pitchDeckV2ByChat does for decks. */
  htmlPageBusyByChat: Record<string, boolean>
  setHtmlPageBusy: (chatId: string, busy: boolean) => void
  /** The generated page the side panel has open. A freshly generated card
   *  carries no session id, so the panel publishes it once it has one. */
  openHtmlPageSessionId: string
  setOpenHtmlPageSessionId: (sessionId: string) => void
  pitchDeckV2ByChat: Record<string, PitchDeckV2ChatState>
  setPitchDeckV2ChatState: (
    chatId: string,
    patch: Partial<PitchDeckV2ChatState>
  ) => void
  pptOutlineState: {
    isStreaming: boolean
    cards: any[]
    sessionId: string | null
    params: any | null
    hasStarted: boolean
    slidesStatus: "idle" | "generating" | "ready"
    slides: any[]
    requestedCount: number
  }
  setPptOutlineState: (
    value: Partial<{
      isStreaming: boolean
      cards: any[]
      sessionId: string | null
      params: any | null
      hasStarted: boolean
      slidesStatus: "idle" | "generating" | "ready"
      slides: any[]
      requestedCount: number
    }>
  ) => void
  resetPptOutlineState: () => void
  // Suggestions deferred until the PptBuilderV2 side panel signals slide
  // images + pptx are ready. Keyed by chat sessionId — one in-flight
  // PPT generation per chat at a time. AgentPptOpusPanel.onDone reads
  // and clears once it fires the deferred fetch.
  pendingPptSuggestions: Record<
    string,
    {
      invocationId: string
      assistantMessageId: number
      surface: string
      accountId: string
    }
  >
  setPendingPptSuggestion: (
    sessionId: string,
    value: {
      invocationId: string
      assistantMessageId: number
      surface: string
      accountId: string
    } | null
  ) => void
}

const useOSAgentsIdStore = create<IUseOSAgentsIdStore>((set) => ({
  messages: {},
  setMessages: (key, value) =>
    set((state) => {
      const existingMessages = state.messages[key] ?? {}
      const existingMessage = existingMessages[value.id] ?? {}
      return {
        messages: {
          ...state.messages,
          [key]: {
            ...existingMessages,
            [value.id]: {
              ...existingMessage,
              ...value
            }
          }
        }
      }
    }),
  openChatHistroy: false,
  setOpenChatHistroy: (value) => set({ openChatHistroy: value }),
  showSplitter: null,
  setShowSplitter: (value) => set({ showSplitter: value }),
  elapsedTimes: {},
  setElapsedTime: (key, value) =>
    set((state) => ({
      elapsedTimes: {
        ...state.elapsedTimes,
        [key]: (state.elapsedTimes[key] || 0) + value
      }
    })),
  uploadedFiles: [],
  setUploadedFiles: (files: File[]) =>
    set((state) => ({ uploadedFiles: [...state.uploadedFiles, ...files] })),
  removeUploadedFile: (file: File) =>
    set((state) => ({
      uploadedFiles: state.uploadedFiles.filter((f) => f !== file)
    })),
  clearUploadedFiles: () => set({ uploadedFiles: [] }),
  pendingMessage: "",
  setPendingMessage: (value) => set({ pendingMessage: value }),
  quotedText: null,
  setQuotedText: (value) => set({ quotedText: value }),
  sourcesDrawerData: null,
  setSourcesDrawerData: (sources) => set({ sourcesDrawerData: sources }),
  pitchDeckSlideImages: [],
  setPitchDeckSlideImages: (urls) => set({ pitchDeckSlideImages: urls }),
  pitchDeckTitleBySession: {},
  setPitchDeckTitle: (pptSessionId, title) =>
    set((state) =>
      !pptSessionId || state.pitchDeckTitleBySession[pptSessionId] === title
        ? state
        : {
            pitchDeckTitleBySession: {
              ...state.pitchDeckTitleBySession,
              [pptSessionId]: title
            }
          }
    ),
  openHtmlPageSessionId: "",
  setOpenHtmlPageSessionId: (sessionId) =>
    set((state) =>
      state.openHtmlPageSessionId === sessionId
        ? state
        : { openHtmlPageSessionId: sessionId }
    ),
  htmlPageBusyByChat: {},
  setHtmlPageBusy: (chatId, busy) =>
    set((state) =>
      !!state.htmlPageBusyByChat[chatId] === busy
        ? state
        : {
            htmlPageBusyByChat: {
              ...state.htmlPageBusyByChat,
              [chatId]: busy
            }
          }
    ),
  pitchDeckV2ByChat: {},
  setPitchDeckV2ChatState: (chatId, patch) =>
    set((state) => {
      const prev = state.pitchDeckV2ByChat[chatId] ?? {
        pptSessionId: null,
        status: "idle" as const,
        statusKey: "",
        slideImages: [],
        expectedSlideCount: 0,
        errorMessage: ""
      }
      return {
        pitchDeckV2ByChat: {
          ...state.pitchDeckV2ByChat,
          [chatId]: { ...prev, ...patch }
        }
      }
    }),
  pptOutlineState: {
    isStreaming: false,
    cards: [],
    sessionId: null,
    params: null,
    hasStarted: false,
    slidesStatus: "idle" as const,
    slides: [],
    requestedCount: 8
  },
  setPptOutlineState: (value) =>
    set((state) => ({
      pptOutlineState: { ...state.pptOutlineState, ...value }
    })),
  resetPptOutlineState: () =>
    set({
      pptOutlineState: {
        isStreaming: false,
        cards: [],
        sessionId: null,
        params: null,
        hasStarted: false,
        slidesStatus: "idle" as const,
        slides: [],
        requestedCount: 8
      }
    }),
  pendingPptSuggestions: {},
  setPendingPptSuggestion: (sessionId, value) =>
    set((state) => {
      const next = { ...state.pendingPptSuggestions }
      if (value === null) {
        delete next[sessionId]
      } else {
        next[sessionId] = value
      }
      return { pendingPptSuggestions: next }
    }),
  resetOSAgentsIdStore: (_sessionId: string) =>
    // DO NOT wipe `messages[sessionId]` — when a user navigates from
    // /os/agents/A to /os/agents/B and back, the route's useEffect
    // cleanup fires for A; wiping messages here loses the in-flight
    // stream's accumulated step rows. The store is keyed by sessionId
    // anyway, so messages for other sessions don't interfere; bounded
    // memory by # of distinct sessions the user has visited this tab.
    //
    // Auxiliary panel/chrome state IS still reset — those are global
    // (not per-session) and need clearing on session boundaries to
    // avoid leaking the previous session's open splitter / open
    // history drawer into the new one.
    set(() => ({
      showSplitter: null,
      openChatHistroy: false,
      pitchDeckSlideImages: [],
      htmlPageBusyByChat: {},
      openHtmlPageSessionId: ""
    }))
}))

export default useOSAgentsIdStore
